import { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { useBrandingStore } from '../store/branding'

interface ProposalData {
  proposal: {
    id: string; title: string; status: string
    subtotal: number; tax_rate: number; tax_amount: number; total: number
    cover_image_r2_key: string | null; about_us: string | null
    project_description: string | null; accepted_at: string | null
    accepted_by_name: string | null; selected_options: string | null
    branding_override: string | null
    created_at: string
  }
  client: { company_name: string; contact_name: string; email: string; phone: string; address: string }
  prepared_by: string
  areas: Array<{
    id: string; name: string; sort_order: number
    option_labels: string | null; client_description: string | null
    area_subtotal: number
  }>
  line_items: Array<{
    id: string; area_id: string; name: string; description: string
    manufacturer: string; sell_price: number; quantity: number
    item_type: string; option_index: number; is_tax_exempt: number
    catalog_image_r2_key: string | null; image_r2_key: string | null
  }>
  payments: Array<{
    label: string; amount_type: string; amount_value: number
    milestone_name: string; calculated_amount: number
  }>
}

const brandColors: Record<string, string> = {
  'Hikvision': '#cc0000', 'Ubiquiti': '#2563eb', 'Dell': '#007db8',
  'Denon': '#1a1a2e', 'Sonance': '#2d5a27', 'JL Audio': '#8b0000',
  'Sony': '#000000', 'Wavelynx': '#1e3a5f', 'Orion Access': '#d4880f',
  'APC': '#ff6600', 'Monoprice': '#4b5563',
}

function getBrandColor(brand?: string): string {
  if (!brand) return '#6b7280'
  return brandColors[brand] || `hsl(${brand.charCodeAt(0) * 7 % 360}, 45%, 45%)`
}

function fmtMoney(n: number): string {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

export default function ClientProposalView() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  // ?print=1 is how "Download PDF" reaches this page: render the document,
  // then hand it straight to the browser's print/save-as-PDF dialog.
  const printMode = searchParams.get('print') === '1'
  const [data, setData] = useState<ProposalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAccept, setShowAccept] = useState(false)
  const [showChanges, setShowChanges] = useState(false)
  const [changesNote, setChangesNote] = useState('')
  const [signName, setSignName] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [collapsedAreas, setCollapsedAreas] = useState<Set<string>>(new Set())
  const [selectedOptions, setSelectedOptions] = useState<Record<string, number>>({})
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawing = useRef(false)
  const tenantBranding = useBrandingStore()

  const loadProposal = useCallback(async () => {
    try {
      const res = await fetch(`/api/p/${token}`)
      if (!res.ok) { setError('Proposal not found'); return }
      const d = await res.json() as ProposalData
      setData(d)
      if (d.proposal.accepted_at) setAccepted(true)
      // Initialize selected options from proposal data
      if (d.proposal.selected_options) {
        try {
          const parsed = JSON.parse(d.proposal.selected_options)
          setSelectedOptions(parsed)
        } catch { /* use defaults */ }
      }
    } catch { setError('Failed to load proposal') }
    finally { setLoading(false) }
  }, [token])

  useEffect(() => { loadProposal() }, [loadProposal])

  // Auto-open the print dialog once the document is actually ready to print.
  // Firing on render is too early: product images are still in flight and the
  // browser would rasterise half-empty rows into the PDF. Wait for the images
  // to settle (with a ceiling, so one broken image cannot hang the download).
  useEffect(() => {
    if (!printMode || !data) return
    let cancelled = false

    const images = Array.from(document.images)
    const pending = images
      .filter((img) => !img.complete)
      .map((img) => new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true })
        img.addEventListener('error', () => resolve(), { once: true })
      }))

    const settled = Promise.all(pending)
    const ceiling = new Promise<void>((resolve) => setTimeout(resolve, 3000))

    Promise.race([settled, ceiling]).then(() => {
      if (cancelled) return
      // One more frame so the print stylesheet has been applied.
      requestAnimationFrame(() => { if (!cancelled) window.print() })
    })

    return () => { cancelled = true }
  }, [printMode, data])

  // Apply client accent color from branding override
  useEffect(() => {
    if (data?.proposal?.branding_override) {
      try {
        const override = JSON.parse(data.proposal.branding_override)
        if (override.accentColor) {
          document.documentElement.style.setProperty('--client-accent', override.accentColor)
          document.documentElement.style.setProperty('--accent', override.accentColor)
          document.documentElement.style.setProperty('--primary', override.accentColor)
        }
      } catch { /* ignore */ }
    }
  }, [data?.proposal?.branding_override])

  // Engagement tracking [POR-50]
  useEffect(() => {
    if (!token || !data) return
    let maxScroll = 0
    const startTime = Date.now()

    const handleScroll = () => {
      const pct = Math.round((window.scrollY + window.innerHeight) / document.body.scrollHeight * 100)
      maxScroll = Math.max(maxScroll, pct)
    }

    const sendBeacon = () => {
      const duration = Math.round((Date.now() - startTime) / 1000)
      navigator.sendBeacon(`/api/p/${token}/engagement`, JSON.stringify({
        duration,
        scroll_depth: maxScroll,
      }))
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('beforeunload', sendBeacon)
    // Also send on visibility change (for mobile)
    const handleVisibility = () => { if (document.visibilityState === 'hidden') sendBeacon() }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('beforeunload', sendBeacon)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [token, data])

  function toggleArea(areaId: string) {
    setCollapsedAreas(prev => {
      const next = new Set(prev)
      if (next.has(areaId)) next.delete(areaId)
      else next.add(areaId)
      return next
    })
  }

  function selectOption(areaId: string, optionIndex: number) {
    setSelectedOptions(prev => ({ ...prev, [areaId]: optionIndex }))
  }

  function startDrawing(e: React.MouseEvent | React.TouchEvent) {
    const canvas = canvasRef.current
    if (!canvas) return
    isDrawing.current = true
    const ctx = canvas.getContext('2d')!
    const rect = canvas.getBoundingClientRect()
    const pt = 'touches' in e ? e.touches[0] : e
    ctx.beginPath()
    ctx.moveTo(pt.clientX - rect.left, pt.clientY - rect.top)
  }

  function draw(e: React.MouseEvent | React.TouchEvent) {
    if (!isDrawing.current || !canvasRef.current) return
    const ctx = canvasRef.current.getContext('2d')!
    const rect = canvasRef.current.getBoundingClientRect()
    const pt = 'touches' in e ? e.touches[0] : e
    ctx.strokeStyle = 'var(--text-primary)'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineTo(pt.clientX - rect.left, pt.clientY - rect.top)
    ctx.stroke()
  }

  function stopDrawing() { isDrawing.current = false }

  function clearSignature() {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height)
  }

  async function handleAccept() {
    if (!signName.trim() || !canvasRef.current) return
    setSubmitting(true)
    try {
      const sigData = canvasRef.current.toDataURL('image/png')
      const res = await fetch(`/api/p/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: signName, signature_data: sigData, selected_options: selectedOptions }),
      })
      if (res.ok) { setAccepted(true); setShowAccept(false) }
    } catch { /* handled */ }
    finally { setSubmitting(false) }
  }

  async function handleRequestChanges() {
    if (!changesNote.trim()) return
    setSubmitting(true)
    try {
      await fetch(`/api/p/${token}/request-changes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: changesNote }),
      })
      setShowChanges(false)
      setChangesNote('')
      loadProposal()
    } catch { /* handled */ }
    finally { setSubmitting(false) }
  }

  if (loading) return (
    <div className="cpv-loading">
      <div className="cpv-spinner" />
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 16 }}>Loading proposal...</p>
    </div>
  )
  if (error || !data) return (
    <div className="cpv-loading">
      <p style={{ color: 'var(--danger)', fontSize: 14 }}>{error || 'Not found'}</p>
    </div>
  )

  const { proposal, client, prepared_by, areas, line_items, payments } = data

  // Load the branding for this proposal
  const proposalBranding = proposal.branding_override
    ? (() => { try { return JSON.parse(proposal.branding_override) } catch { return null } })()
    : null

  // Merged branding (override wins where present)
  const branding = {
    companyName: proposalBranding?.companyName || tenantBranding.companyName,
    logo: proposalBranding?.logoR2Key
      ? `/api/files/${proposalBranding.logoR2Key}`
      : tenantBranding.logoIcon,
    accentColor: proposalBranding?.accentColor || tenantBranding.accentColor,
    address: proposalBranding?.address || '',
    cityStateZip: proposalBranding?.cityStateZip || '',
    email: proposalBranding?.email || '',
    phone: proposalBranding?.phone || '',
    footerText: proposalBranding?.footerText || tenantBranding.footerText,
  }

  // Calculate totals considering option selections
  const getAreaItems = (area: typeof areas[0]) => {
    const areaItems = line_items.filter(li => li.area_id === area.id)
    const options = area.option_labels ? area.option_labels.split(',').map(s => s.trim()) : null
    const activeOption = selectedOptions[area.id] ?? 0
    if (options && options.length > 1) {
      return areaItems.filter(li => li.option_index === activeOption || li.option_index === -1)
    }
    return areaItems
  }

  return (
    <div className="cpv-page">
      {/* ── Sticky Header ── */}
      <header className="cpv-header" style={branding.accentColor ? { borderBottomColor: branding.accentColor } : undefined}>
        <div className="cpv-header-inner">
          <img src={branding.logo || '/Amber Global icon.png'} alt={branding.companyName} className="cpv-header-logo" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cpv-header-company">{branding.companyName}</div>
            {branding.cityStateZip && (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>{branding.cityStateZip}</div>
            )}
          </div>
          <div className="cpv-header-total">{fmtMoney(proposal.total)}</div>
        </div>
      </header>

      {/* ── Proposal Title Section ── */}
      <div className="cpv-content">
        <div className="cpv-title-section">
          <div className="cpv-title-label">Proposal</div>
          <h1 className="cpv-title">{proposal.title}</h1>
          <div className="cpv-title-meta">
            Prepared for {client.contact_name || client.company_name}
            <br />
            {new Date(proposal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          {prepared_by && <div className="cpv-title-meta" style={{ marginTop: 4 }}>Prepared by {prepared_by}</div>}
        </div>

        {/* ── Cover Image ── */}
        {proposal.cover_image_r2_key && (
          <img
            src={proposal.cover_image_r2_key.startsWith('http') ? proposal.cover_image_r2_key : `/api/files/${proposal.cover_image_r2_key}`}
            alt="Cover"
            className="w-full max-w-full h-auto object-cover rounded-lg"
            style={{ maxWidth: '100%', maxHeight: 256, marginBottom: 20 }}
          />
        )}

        {/* ── Accepted Banner ── */}
        {accepted && (
          <div className="cpv-accepted-banner">
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--success)' }}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4 12 14.01l-3-3" />
            </svg>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--success)' }}>Proposal Accepted</div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                Accepted by {proposal.accepted_by_name} on {proposal.accepted_at ? new Date(proposal.accepted_at).toLocaleDateString() : ''}
              </p>
            </div>
          </div>
        )}

        {/* ── Project Description ── */}
        {proposal.project_description && (
          <div className="cpv-card" style={{ marginBottom: 16 }}>
            <div className="cpv-card-header">
              <span className="cpv-card-header-title">Project Description</span>
            </div>
            <div className="cpv-card-body">
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{proposal.project_description}</p>
            </div>
          </div>
        )}

        {/* ── Areas (Collapsible) ── */}
        {areas.map(area => {
          const isCollapsed = !printMode && collapsedAreas.has(area.id)
          const options = area.option_labels ? area.option_labels.split(',').map(s => s.trim()) : null
          const activeOption = selectedOptions[area.id] ?? 0
          const visibleItems = getAreaItems(area)
          const areaTotal = visibleItems.reduce((sum, li) => sum + li.sell_price * li.quantity, 0)

          return (
            <div key={area.id} className="cpv-area-card">
              {/* Area Header — Collapsible */}
              <button className="cpv-area-header" onClick={() => toggleArea(area.id)}>
                <svg
                  width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
                  className="cpv-chevron"
                  style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
                <span className="cpv-area-name">{area.name}</span>
                <span className="cpv-area-total">{fmtMoney(areaTotal)}</span>
              </button>

              {/* Option Toggle Pills */}
              {!isCollapsed && options && options.length > 1 && (
                <div className="cpv-option-pills-wrap">
                  <div className="cpv-option-pills">
                    {options.map((opt, i) => (
                      <button
                        key={i}
                        className={`cpv-option-pill ${activeOption === i ? 'cpv-option-pill-active' : ''}`}
                        onClick={() => selectOption(area.id, i)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Area Description */}
              {!isCollapsed && area.client_description && (
                <div className="cpv-area-description">{area.client_description}</div>
              )}

              {/* Line Items — Product Cards */}
              {!isCollapsed && visibleItems.map((item, i) => (
                <div key={item.id} className="cpv-product-card" style={{ borderBottom: i < visibleItems.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <ProductImg item={item} size={48} />
                  <div className="cpv-product-info">
                    <div className="cpv-product-name">{item.name}</div>
                    {item.manufacturer && (
                      <div className="cpv-product-brand">
                        <span className="cpv-brand-dot" style={{ background: getBrandColor(item.manufacturer) }} />
                        {item.manufacturer}
                        {item.description && <span style={{ color: 'var(--text-tertiary)' }}> · {item.description}</span>}
                      </div>
                    )}
                    <div className="cpv-product-price-row">
                      <span className="cpv-product-qty">Qty: {item.quantity}</span>
                      <span className="cpv-product-price">{fmtMoney(item.sell_price * item.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Area Total Footer */}
              {!isCollapsed && visibleItems.length > 0 && (
                <div className="cpv-area-footer">
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Area Total</span>
                  <span className="cpv-area-footer-total">{fmtMoney(areaTotal)}</span>
                </div>
              )}
            </div>
          )
        })}

        {/* ── Financial Summary ── */}
        <div className="cpv-card cpv-financial-summary">
          <div className="cpv-card-header">
            <span className="cpv-card-header-title">Financial Summary</span>
          </div>
          <div className="cpv-card-body">
            <div className="cpv-summary-row">
              <span>Subtotal</span>
              <span className="cpv-summary-value">{fmtMoney(proposal.subtotal)}</span>
            </div>
            <div className="cpv-summary-row">
              <span>Tax (<span style={{ fontFamily: 'var(--font-mono)' }}>{((proposal.tax_rate || 0) * 100).toFixed(2)}%</span>)</span>
              <span className="cpv-summary-value">{fmtMoney(proposal.tax_amount)}</span>
            </div>
            <div className="cpv-summary-total-row">
              <span>Project Total</span>
              <span className="cpv-summary-total-value">{fmtMoney(proposal.total)}</span>
            </div>
          </div>
        </div>

        {/* ── Payment Schedule ── */}
        {payments.length > 0 && (
          <div className="cpv-card">
            <div className="cpv-card-header">
              <span className="cpv-card-header-title">Payment Schedule</span>
            </div>
            <div className="cpv-card-body">
              {payments.map((p, i) => (
                <div key={i} className="cpv-payment-row" style={{ borderBottom: i < payments.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{p.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {p.amount_type === 'percentage' ? <span style={{ fontFamily: 'var(--font-mono)' }}>{p.amount_value}%</span> : p.amount_type === 'balance' ? 'Outstanding balance' : <span style={{ fontFamily: 'var(--font-mono)' }}>{fmtMoney(p.amount_value)}</span>}
                      {p.amount_type === 'percentage' && ' of total'}
                      {p.milestone_name && ` · Due upon ${p.milestone_name}`}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>
                    {fmtMoney(p.calculated_amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── About Us ── */}
        {proposal.about_us && (
          <div className="cpv-card" style={{ marginTop: 16 }}>
            <div className="cpv-card-header">
              <span className="cpv-card-header-title">About {branding.companyName}</span>
            </div>
            <div className="cpv-card-body">
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{proposal.about_us}</p>
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="cpv-footer">
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{branding.footerText || `Powered by ${branding.companyName}`}</div>
          {(branding.email || branding.phone) && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
              {[branding.email, branding.phone].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>

      {/* ── Sticky Bottom Action Bar ── */}
      {!accepted && (
        <div className="cpv-bottom-bar">
          <button className="cpv-btn-secondary" onClick={() => setShowChanges(true)}>
            Request Changes
          </button>
          <button className="cpv-btn-accept" onClick={() => setShowAccept(true)}>
            Accept Proposal
          </button>
        </div>
      )}

      {/* ── Accept Bottom Sheet Modal ── */}
      {showAccept && (
        <ModalOverlay onClose={() => setShowAccept(false)}>
          <div className="cpv-modal-handle" />
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Accept Proposal</div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
            Sign below to accept the project design, terms, and payment schedule.
          </p>
          <div style={{ marginBottom: 16 }}>
            <label className="cpv-label">Your Full Name</label>
            <input
              value={signName}
              onChange={e => setSignName(e.target.value)}
              placeholder="Full name"
              className="cpv-input"
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label className="cpv-label">Signature</label>
            <canvas
              ref={canvasRef} width={460} height={160}
              className="cpv-signature-canvas"
              onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
              onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing}
            />
            <button onClick={clearSignature} className="cpv-clear-sig">Clear signature</button>
          </div>
          <button
            onClick={handleAccept}
            disabled={submitting || !signName.trim()}
            className="cpv-btn-accept"
            style={{ width: '100%', opacity: submitting || !signName.trim() ? 0.5 : 1 }}
          >
            {submitting ? 'Submitting...' : 'Submit and Accept'}
          </button>
        </ModalOverlay>
      )}

      {/* ── Changes Bottom Sheet Modal ── */}
      {showChanges && (
        <ModalOverlay onClose={() => setShowChanges(false)}>
          <div className="cpv-modal-handle" />
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Request Changes</div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Describe the changes you'd like made to this proposal.</p>
          <textarea
            value={changesNote}
            onChange={e => setChangesNote(e.target.value)}
            placeholder="Describe changes..."
            rows={4}
            className="cpv-textarea"
          />
          <Button
            onClick={handleRequestChanges}
            disabled={submitting || !changesNote.trim()}
            loading={submitting}
            style={{
              width: '100%', padding: '14px 0', borderRadius: 'var(--radius)', border: 'none',
              fontSize: 16, fontWeight: 600, cursor: 'pointer',
              marginTop: 12, opacity: submitting || !changesNote.trim() ? 0.5 : 1,
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </ModalOverlay>
      )}
    </div>
  )
}

function ProductImg({ item, size = 40 }: { item: { manufacturer: string; item_type: string; catalog_image_r2_key?: string | null; image_r2_key?: string | null }; size?: number }) {
  if (item.catalog_image_r2_key || item.image_r2_key) {
    return <img src={`/api/files/${item.catalog_image_r2_key || item.image_r2_key}`} alt="" className="cpv-product-img" style={{ width: size, height: size }} />
  }
  const isLabor = item.item_type === 'labor'
  const bg = isLabor ? '#6b7280' : getBrandColor(item.manufacturer)
  return (
    <div className="cpv-product-img cpv-product-img-placeholder" style={{ width: size, height: size, background: `linear-gradient(135deg, ${bg}dd, ${bg})` }}>
      {isLabor ? 'LBR' : (item.manufacturer?.slice(0, 3) || '?')}
    </div>
  )
}

function ModalOverlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="cpv-modal-overlay" onClick={onClose}>
      <div className="cpv-modal-sheet" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}
